'use client'

import { useCallback, useState, useEffect } from 'react'
import { useWallet } from '@tronweb3/tronwallet-adapter-react-hooks'
import { useTronContext } from '@/lib/tron-provider'
import { useAppKit } from '@reown/appkit/react'
import { useAppKitNetwork } from '@reown/appkit/react'
import { activeTronNetwork } from '@/lib/appkit-init'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ArrowLeft, ExternalLink, Download } from 'lucide-react'
import toast from 'react-hot-toast'

interface TronWalletModalProps {
    open: boolean
    onClose: () => void
}

// =============== WALLET INSTALL URLs ===============
const WALLET_INSTALL_URLS: Record<string, string> = {
    'TronLink': 'https://www.tronlink.org/',
    'Ledger': 'https://www.ledger.com/',
    'BitKeep': 'https://bitkeep.com/',
    'Bitget': 'https://bitkeep.com/',
    'OKX Wallet': 'https://www.okx.com/web3',
    'OkxWallet': 'https://www.okx.com/web3',
    'TokenPocket': 'https://www.tokenpocket.pro/',
    'Trust': 'https://trustwallet.com/',
    'Trust Wallet': 'https://trustwallet.com/',
    'imToken': 'https://token.im/',
    'imToken Wallet': 'https://token.im/',
    'Bybit': 'https://www.bybit.com/web3/',
    'Bybit Wallet': 'https://www.bybit.com/web3/',
    'FoxWallet': 'https://foxwallet.com/',
    'Gate Wallet': 'https://www.gate.io/web3',
    'GateWallet': 'https://www.gate.io/web3',
    'Binance Wallet': 'https://www.binance.com/en/web3wallet',
    'Tomo Wallet': 'https://tomo.inc/',
    'TomoWallet': 'https://tomo.inc/',
    'MetaMask': 'https://metamask.io/',
    'Guarda': 'https://guarda.com/',
}

// =============== WALLET ICONS ===============
const TronLinkIcon = () => (
    <svg viewBox="0 0 40 40" className="w-full h-full">
        <defs>
            <linearGradient id="tronlink-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#2AABEE" />
                <stop offset="100%" stopColor="#1C8DD9" />
            </linearGradient>
        </defs>
        <circle cx="20" cy="20" r="20" fill="url(#tronlink-grad)" />
        <path d="M12 14L20 10L28 14L20 30L12 14Z" fill="white" />
        <path d="M20 10L28 14L20 18V10Z" fill="white" fillOpacity="0.7" />
    </svg>
)

const WalletConnectIcon = () => (
    <svg viewBox="0 0 40 40" className="w-full h-full">
        <circle cx="20" cy="20" r="20" fill="#3B99FC" />
        <path d="M13 18.5C16.5-3 23.5-3 27 18.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" fill="none" transform="translate(0, 3)" />
        <path d="M16 20C18-1.5 22-1.5 24 20" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" transform="translate(0, 1)" />
    </svg>
)

const LedgerIcon = () => (
    <svg viewBox="0 0 40 40" className="w-full h-full">
        <rect width="40" height="40" rx="8" fill="#000" />
        <rect x="10" y="12" width="12" height="8" rx="1" fill="white" />
        <rect x="10" y="22" width="20" height="6" rx="1" fill="white" />
        <rect x="24" y="14" width="6" height="4" rx="0.5" fill="white" />
    </svg>
)

const MoreWalletsIcon = () => (
    <svg viewBox="0 0 40 40" className="w-full h-full">
        <rect width="40" height="40" rx="10" fill="#2D3748" />
        <circle cx="12" cy="20" r="3" fill="#E53E3E" />
        <circle cx="20" cy="20" r="3" fill="#38A169" />
        <circle cx="28" cy="20" r="3" fill="#3182CE" />
    </svg>
)

const BitgetIcon = () => (
    <svg viewBox="0 0 40 40" className="w-full h-full">
        <rect width="40" height="40" rx="8" fill="#00D4AA" />
        <path d="M10 12L20 8L30 12V28L20 32L10 28V12Z" fill="white" />
        <path d="M15 16L20 14L25 16V24L20 26L15 24V16Z" fill="#00D4AA" />
    </svg>
)

const OKXIcon = () => (
    <svg viewBox="0 0 40 40" className="w-full h-full">
        <rect width="40" height="40" rx="8" fill="#000" />
        <rect x="10" y="10" width="8" height="8" fill="white" />
        <rect x="22" y="10" width="8" height="8" fill="white" />
        <rect x="16" y="16" width="8" height="8" fill="white" />
        <rect x="10" y="22" width="8" height="8" fill="white" />
        <rect x="22" y="22" width="8" height="8" fill="white" />
    </svg>
)

const TokenPocketIcon = () => (
    <svg viewBox="0 0 40 40" className="w-full h-full">
        <circle cx="20" cy="20" r="20" fill="#2980FE" />
        <path d="M14 12H26V14H14V12ZM16 16H24V28H20V20H18V28H16V16Z" fill="white" />
    </svg>
)

const TrustIcon = () => (
    <svg viewBox="0 0 40 40" className="w-full h-full">
        <circle cx="20" cy="20" r="20" fill="#3375BB" />
        <path d="M20 8C12 12 12 28 20 32C28 28 28 12 20 8Z" fill="white" />
        <path d="M20 8C16 10 15 22 20 26C25 22 24 10 20 8Z" fill="#3375BB" />
    </svg>
)

const ImTokenIcon = () => (
    <svg viewBox="0 0 40 40" className="w-full h-full">
        <circle cx="20" cy="20" r="20" fill="#11C4D1" />
        <circle cx="20" cy="20" r="10" fill="white" />
        <circle cx="20" cy="20" r="5" fill="#11C4D1" />
    </svg>
)

const BybitIcon = () => (
    <svg viewBox="0 0 40 40" className="w-full h-full">
        <rect width="40" height="40" rx="8" fill="#F7A600" />
        <path d="M12 14H18V20H12V14ZM22 14H28L25 20H22V14ZM12 24H18L15 30H12V24ZM22 24H28V30H22V24Z" fill="white" />
    </svg>
)

const FoxWalletIcon = () => (
    <svg viewBox="0 0 40 40" className="w-full h-full">
        <circle cx="20" cy="20" r="20" fill="#FF8B3E" />
        <path d="M12 15L20 10L28 15L26 22L20 28L14 22L12 15Z" fill="white" />
        <circle cx="16" cy="18" r="2" fill="#FF8B3E" />
        <circle cx="24" cy="18" r="2" fill="#FF8B3E" />
    </svg>
)

const GateIcon = () => (
    <svg viewBox="0 0 40 40" className="w-full h-full">
        <rect width="40" height="40" rx="8" fill="#17E6A1" />
        <path d="M20 8L32 14V26L20 32L8 26V14L20 8Z" fill="white" />
        <path d="M20 14L26 17V23L20 26L14 23V17L20 14Z" fill="#17E6A1" />
    </svg>
)

const BinanceIcon = () => (
    <svg viewBox="0 0 40 40" className="w-full h-full">
        <rect width="40" height="40" rx="8" fill="#F0B90B" />
        <path d="M20 10L24 14L20 18L16 14L20 10Z" fill="white" />
        <path d="M12 18L16 22L12 26L8 22L12 18Z" fill="white" />
        <path d="M28 18L32 22L28 26L24 22L28 18Z" fill="white" />
        <path d="M20 22L24 26L20 30L16 26L20 22Z" fill="white" />
    </svg>
)

const TomoIcon = () => (
    <svg viewBox="0 0 40 40" className="w-full h-full">
        <circle cx="20" cy="20" r="20" fill="#8B5CF6" />
        <path d="M15 12H25V16H22V28H18V16H15V12Z" fill="white" />
    </svg>
)

const MetaMaskIcon = () => (
    <svg viewBox="0 0 40 40" className="w-full h-full">
        <rect width="40" height="40" rx="8" fill="#F5841F" />
        <path d="M32 10L22 18L24 14L32 10Z" fill="white" />
        <path d="M8 10L18 18L16 14L8 10Z" fill="white" />
        <path d="M28 26L24 32L32 30L28 26Z" fill="white" />
        <path d="M12 26L16 32L8 30L12 26Z" fill="white" />
        <path d="M18 20L16 24L24 24L22 20L18 20Z" fill="white" />
    </svg>
)

const GuardaIcon = () => (
    <svg viewBox="0 0 40 40" className="w-full h-full">
        <circle cx="20" cy="20" r="20" fill="#1A73E8" />
        <path d="M20 8L30 14V22C30 28 20 32 20 32C20 32 10 28 10 22V14L20 8Z" fill="white" />
        <path d="M20 12L26 16V21C26 25 20 28 20 28C20 28 14 25 14 21V16L20 12Z" fill="#1A73E8" />
    </svg>
)

// Wallet icon mapping by adapter name
const walletIcons: Record<string, React.ReactNode> = {
    'TronLink': <TronLinkIcon />,
    'Ledger': <LedgerIcon />,
    'BitKeep': <BitgetIcon />,
    'Bitget': <BitgetIcon />,
    'Bitget Wallet': <BitgetIcon />,
    'OKX Wallet': <OKXIcon />,
    'OkxWallet': <OKXIcon />,
    'TokenPocket': <TokenPocketIcon />,
    'Trust': <TrustIcon />,
    'Trust Wallet': <TrustIcon />,
    'imToken': <ImTokenIcon />,
    'imToken Wallet': <ImTokenIcon />,
    'Bybit': <BybitIcon />,
    'Bybit Wallet': <BybitIcon />,
    'FoxWallet': <FoxWalletIcon />,
    'Gate Wallet': <GateIcon />,
    'GateWallet': <GateIcon />,
    'Binance Wallet': <BinanceIcon />,
    'Tomo Wallet': <TomoIcon />,
    'TomoWallet': <TomoIcon />,
    'MetaMask': <MetaMaskIcon />,
    'Guarda': <GuardaIcon />,
}

// Get wallet icon with fallback
const getWalletIcon = (name: string) => {
    if (walletIcons[name]) return walletIcons[name]
    const colors = ['#E53E3E', '#DD6B20', '#38A169', '#3182CE', '#805AD5', '#D53F8C']
    const colorIndex = name.charCodeAt(0) % colors.length
    return (
        <svg viewBox="0 0 40 40" className="w-full h-full">
            <circle cx="20" cy="20" r="20" fill={colors[colorIndex]} />
            <text x="20" y="26" textAnchor="middle" fill="white" fontSize="18" fontWeight="bold">
                {name.charAt(0)}
            </text>
        </svg>
    )
}

/**
 * Custom Tron Wallet Modal - Responsive with 4x4 Grid for More Wallets
 */
export function TronWalletModal({ open, onClose }: TronWalletModalProps) {
    const { wallets, select, connecting } = useWallet()
    const { enableAdapters } = useTronContext()
    const { open: openAppKit } = useAppKit()
    const { switchNetwork } = useAppKitNetwork()
    const [showAllWallets, setShowAllWallets] = useState(false)

    // Enable adapters when modal opens - this triggers lazy loading
    // This is the ONLY time adapters are created, preventing auto-connection on page load
    useEffect(() => {
        if (open) {
            enableAdapters()
        }
    }, [open, enableAdapters])

    // Find specific adapters
    const tronLinkAdapter = wallets.find(w => w.adapter.name === 'TronLink')
    const ledgerAdapter = wallets.find(w => w.adapter.name === 'Ledger')

    // Check if wallet is ready for connection attempt - includes Loading for async detection
    // TronWeb3 WalletReadyState only has: Loading, NotFound, Found
    const isWalletReady = (wallet: typeof wallets[0]) => {
        const state = wallet.adapter.readyState
        // Allow 'Loading' since detection is async, and 'Found' means ready
        return state === 'Found' || state === 'Loading'
    }

    // Check if wallet is actually detected/installed - for showing "Detected" vs "Install" status
    const isWalletDetected = (wallet: typeof wallets[0]) => {
        const state = wallet.adapter.readyState
        // Only 'Found' means actually detected
        return state === 'Found'
    }

    // Handle TronLink connection - always attempt connection, let adapter handle detection
    const handleTronLink = useCallback(async () => {
        if (tronLinkAdapter) {
            try {
                console.log('[TronModal] TronLink readyState:', tronLinkAdapter.adapter.readyState)
                select(tronLinkAdapter.adapter.name)

                // Always try to connect - TronLink adapter handles detection internally
                setTimeout(async () => {
                    try {
                        await tronLinkAdapter.adapter.connect()
                        onClose()
                    } catch (err: unknown) {
                        const error = err as Error
                        console.error('[TronModal] TronLink connect error:', error)

                        // Handle wallet not found - open install page
                        if (error?.message?.includes('not found') ||
                            error?.message?.includes('Not Found') ||
                            error?.name === 'WalletNotFoundError' ||
                            error?.message?.includes('WalletNotFoundError')) {
                            toast.error('TronLink not detected. Opening install page...')
                            window.open(WALLET_INSTALL_URLS['TronLink'], '_blank')
                        } else if (error?.message?.includes('User rejected')) {
                            toast.error('Connection rejected by user')
                        } else {
                            // For other errors, show a generic message
                            toast.error(`TronLink: ${error?.message || 'Connection failed'}`)
                        }
                    }
                }, 200) // Slightly longer delay to allow extension to respond
            } catch (error) {
                console.error('[TronModal] TronLink selection failed:', error)
            }
        } else {
            // No TronLink adapter found - open install page
            toast('Opening TronLink install page...', { icon: '📥' })
            window.open(WALLET_INSTALL_URLS['TronLink'], '_blank')
        }
    }, [tronLinkAdapter, select, onClose])

    // Handle WalletConnect via AppKit — set Tron as active network first
    // so the WC session proposal includes the tron namespace
    const handleWalletConnect = useCallback(async () => {
        try {
            // Switch to Tron network BEFORE opening AppKit
            // This ensures the WC session includes tron namespace
            try {
                switchNetwork(activeTronNetwork)
                console.log('[TronModal] Switched to Tron network before WC connect')
            } catch (e) {
                console.warn('[TronModal] Failed to pre-switch to Tron:', e)
            }
            onClose()
            await openAppKit()
        } catch (error) {
            console.error('[TronModal] WalletConnect/AppKit failed:', error)
        }
    }, [openAppKit, onClose, switchNetwork])

    // Handle Ledger connection
    const handleLedger = useCallback(async () => {
        if (ledgerAdapter && isWalletReady(ledgerAdapter)) {
            try {
                select(ledgerAdapter.adapter.name)
                await ledgerAdapter.adapter.connect()
                onClose()
            } catch (error) {
                console.error('[TronModal] Ledger connection failed:', error)
                toast.error('Please connect your Ledger device')
            }
        } else {
            toast('Opening Ledger info page...', { icon: '📥' })
            window.open(WALLET_INSTALL_URLS['Ledger'], '_blank')
        }
    }, [ledgerAdapter, select, onClose])

    // Handle generic wallet connection from grid - with better error handling
    const handleWalletClick = useCallback(async (wallet: typeof wallets[0]) => {
        const walletName = wallet.adapter.name
        const installUrl = WALLET_INSTALL_URLS[walletName] || wallet.adapter.url

        // Check if wallet is ready/installed
        if (!isWalletReady(wallet)) {
            toast(`${walletName} not detected. Opening install page...`, { icon: '📥' })
            if (installUrl) {
                window.open(installUrl, '_blank')
            }
            return
        }

        try {
            select(walletName)
            await wallet.adapter.connect()
            onClose()
        } catch (error: unknown) {
            const err = error as Error
            console.error(`[TronModal] ${walletName} connection failed:`, err)

            // Handle "wallet not found" errors gracefully
            if (err?.message?.includes('not found') || err?.name === 'WalletNotFoundError') {
                toast(`${walletName} not detected. Opening install page...`, { icon: '📥' })
                if (installUrl) {
                    window.open(installUrl, '_blank')
                }
            } else {
                toast.error(`Failed to connect ${walletName}`)
            }
        }
    }, [select, onClose])

    // Show More Wallets - use our custom grid view
    const handleMoreWallets = useCallback(() => {
        setShowAllWallets(true)
    }, [])

    // Reset state when modal closes
    const handleClose = useCallback(() => {
        setShowAllWallets(false)
        onClose()
    }, [onClose])

    const primaryWallets = [
        {
            name: 'TronLink',
            icon: <TronLinkIcon />,
            description: tronLinkAdapter && isWalletDetected(tronLinkAdapter) ? 'Detected' : 'Install',
            onClick: handleTronLink,
            available: true,
            installed: tronLinkAdapter && isWalletDetected(tronLinkAdapter),
        },
        {
            name: 'WalletConnect',
            icon: <WalletConnectIcon />,
            description: 'Mobile wallets',
            onClick: handleWalletConnect,
            available: true,
            installed: true,
        },
        {
            name: 'Ledger',
            icon: <LedgerIcon />,
            description: 'Hardware wallet',
            onClick: handleLedger,
            available: true,
            installed: true,
        },
        {
            name: 'More Wallets',
            icon: <MoreWalletsIcon />,
            description: `${wallets.length} available`,
            onClick: handleMoreWallets,
            available: true,
            installed: true,
        },
    ]

    // Animation variants for staggered grid
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.03,
                delayChildren: 0.1,
            }
        }
    }

    const itemVariants = {
        hidden: { opacity: 0, scale: 0.8, y: 10 },
        visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
                type: "spring" as const,
                damping: 20,
                stiffness: 300
            }
        }
    }

    return (
        <AnimatePresence>
            {open && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
                        onClick={handleClose}
                    />

                    {/* Modal - Responsive, centered, not full width */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{
                            type: "spring",
                            damping: 25,
                            stiffness: 350
                        }}
                        className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 
                            mx-4
                            ${showAllWallets
                                ? 'w-[320px] sm:w-[360px]'
                                : 'w-[280px]'
                            }`}
                    >
                        <motion.div
                            layout
                            className="bg-[#1a1a1a] rounded-2xl shadow-2xl border border-white/10 overflow-hidden"
                        >
                            {/* Header */}
                            <motion.div layout className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                                <AnimatePresence mode="wait">
                                    {showAllWallets && (
                                        <motion.button
                                            key="back"
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -10 }}
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            onClick={() => setShowAllWallets(false)}
                                            className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                                        >
                                            <ArrowLeft className="h-4 w-4 text-neutral-400" />
                                        </motion.button>
                                    )}
                                </AnimatePresence>

                                <motion.h3
                                    layout
                                    className="text-white text-sm font-semibold flex-1 text-center"
                                >
                                    {showAllWallets ? 'All Tron Wallets' : 'Connect Tron'}
                                </motion.h3>

                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={handleClose}
                                    className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                                >
                                    <X className="h-4 w-4 text-neutral-400" />
                                </motion.button>
                            </motion.div>

                            {/* Content */}
                            <AnimatePresence mode="wait">
                                {showAllWallets ? (
                                    /* ===== ALL WALLETS 4x4 GRID VIEW ===== */
                                    <motion.div
                                        key="all-wallets"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ duration: 0.2 }}
                                        className="p-3 max-h-[50vh] overflow-y-auto overscroll-contain touch-pan-y"
                                        style={{
                                            WebkitOverflowScrolling: 'touch',
                                            scrollbarWidth: 'thin',
                                            scrollbarColor: 'rgba(255,255,255,0.2) transparent',
                                        }}
                                    >
                                        {/* Grid - 4 columns */}
                                        <motion.div
                                            variants={containerVariants}
                                            initial="hidden"
                                            animate="visible"
                                            className="grid grid-cols-4 gap-2"
                                        >
                                            {wallets.map((wallet) => {
                                                const isReady = isWalletReady(wallet)
                                                return (
                                                    <motion.button
                                                        key={wallet.adapter.name}
                                                        variants={itemVariants}
                                                        onClick={() => handleWalletClick(wallet)}
                                                        disabled={connecting}
                                                        whileHover={{
                                                            scale: 1.05,
                                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                                        }}
                                                        whileTap={{ scale: 0.95 }}
                                                        className={`flex flex-col items-center gap-1 p-2 rounded-xl
                                                            cursor-pointer transition-colors duration-150
                                                            touch-manipulation active:bg-white/15
                                                            ${isReady ? 'bg-white/5 hover:bg-white/10' : 'bg-white/[0.02]'}`}
                                                    >
                                                        {/* Wallet Icon */}
                                                        <div className="w-8 h-8 rounded-lg overflow-hidden relative">
                                                            {getWalletIcon(wallet.adapter.name)}
                                                            {!isReady && (
                                                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                                                    <Download className="w-3 h-3 text-white/70" />
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Wallet Name */}
                                                        <span className={`text-[9px] font-medium text-center leading-tight truncate w-full
                                                            ${isReady ? 'text-white' : 'text-neutral-500'}`}>
                                                            {wallet.adapter.name.replace(' Wallet', '').replace('Wallet', '')}
                                                        </span>

                                                        {/* Status indicator */}
                                                        {wallet.state === 'Connected' && (
                                                            <span className="text-[8px] text-green-400">●</span>
                                                        )}
                                                    </motion.button>
                                                )
                                            })}
                                        </motion.div>

                                        {/* Install note */}
                                        <motion.p
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.3 }}
                                            className="text-neutral-500 text-[10px] text-center mt-3 flex items-center justify-center gap-1"
                                        >
                                            <ExternalLink className="w-3 h-3" />
                                            Tap to connect or install
                                        </motion.p>
                                    </motion.div>
                                ) : (
                                    /* ===== PRIMARY WALLETS LIST VIEW ===== */
                                    <motion.div
                                        key="primary-wallets"
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 20 }}
                                        transition={{ duration: 0.2 }}
                                        className="p-2 space-y-1"
                                    >
                                        {primaryWallets.map((wallet, index) => (
                                            <motion.button
                                                key={wallet.name}
                                                initial={{ opacity: 0, x: -10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: index * 0.05 }}
                                                onClick={wallet.onClick}
                                                disabled={!wallet.available || connecting}
                                                whileHover={{ scale: 1.02, backgroundColor: 'rgba(255,255,255,0.08)' }}
                                                whileTap={{ scale: 0.98 }}
                                                className={`
                                                    w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
                                                    transition-colors duration-150 touch-manipulation
                                                    ${wallet.available
                                                        ? 'bg-white/5 hover:bg-white/10 cursor-pointer active:bg-white/15'
                                                        : 'bg-white/[0.02] cursor-not-allowed opacity-40'
                                                    }
                                                `}
                                            >
                                                <div className="shrink-0 w-8 h-8">{wallet.icon}</div>
                                                <div className="flex-1 text-left">
                                                    <div className="text-white text-sm font-medium">{wallet.name}</div>
                                                    <div className={`text-[11px] ${wallet.installed ? 'text-green-400' : 'text-neutral-500'}`}>
                                                        {wallet.description}
                                                    </div>
                                                </div>
                                                <svg className="w-4 h-4 text-neutral-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                    <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </motion.button>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Footer */}
                            <motion.div layout className="px-4 py-2 border-t border-white/5">
                                <p className="text-neutral-600 text-[10px] text-center">
                                    {showAllWallets
                                        ? `${wallets.length} wallets available`
                                        : 'Secured by TronWeb3'
                                    }
                                </p>
                            </motion.div>
                        </motion.div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    )
}
